import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, CheckCircle2, MailCheck } from "lucide-react";

import StatusPill from "@/components/StatusPill";
import MockBadge from "@/components/MockBadge";
import { admin } from "@/lib/api";
import { fmtTime, fmtUSD, shortId } from "@/lib/format";

export default function Quotes() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(null);

  // Live polling (react-query, 8s).
  const { data: rows = [] } = useQuery({
    queryKey: ["quotes"],
    queryFn: () => admin.quotes(200),
    refetchInterval: 8000,
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["quotes"] });

  return (
    <div data-testid="page-quotes" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· QUILL · Instant Quote Generator</div>
          <h1 className="font-display text-[40px] leading-[1.02]">Quotes</h1>
        </div>
        <div className="shrink-0"><MockBadge /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 pj-card overflow-x-auto">
          <table className="w-full min-w-[640px] mono text-[12px]">
            <thead style={{ color: "rgb(var(--dim))" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-normal">ID</th>
                <th className="px-4 py-3 font-normal">Options</th>
                <th className="px-4 py-3 font-normal">From</th>
                <th className="px-4 py-3 font-normal">Status</th>
                <th className="px-4 py-3 font-normal">Generated</th>
                <th className="px-4 py-3 font-normal">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr
                  key={q.id}
                  data-testid={`quote-row-${q.id}`}
                  style={{ borderTop: "1px solid rgb(var(--ln))" }}
                  className={`cursor-pointer hover:bg-[rgb(236,229,216)] ${open?.id === q.id ? "bg-[rgb(236,229,216)]" : ""}`}
                  onClick={() => setOpen(q)}
                >
                  <td className="px-4 py-3 text-[rgb(var(--dim))] whitespace-nowrap">{shortId(q.id)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{q.options?.length || 0}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {q.options?.[0] ? `${fmtUSD(q.options[0].total_usd)} → ${fmtUSD(q.options[q.options.length-1].total_usd)}` : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusPill state={q.status} /></td>
                  <td className="px-4 py-3 text-[rgb(var(--muted-2))] whitespace-nowrap">{q.generated_in_ms ? `${q.generated_in_ms}ms` : "—"}</td>
                  <td className="px-4 py-3 text-[rgb(var(--muted-2))] whitespace-nowrap">{fmtTime(q.created_at)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 mono text-[11px] text-[rgb(var(--dim))]">No quotes yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <aside className="pj-card p-5 sticky top-6 self-start max-h-[calc(100vh-3rem)] overflow-y-auto">
          {!open && (
            <div className="text-[rgb(var(--dim))] mono text-[12px]">
              <FileText size={14} className="inline mr-2 text-[rgb(var(--muted-2))]" />
              Select a quote to inspect.
            </div>
          )}
          {open && <QuoteDetail quote={open} onChange={refresh} />}
        </aside>
      </div>
    </div>
  );
}

function QuoteDetail({ quote, onChange }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [accepting, setAccepting] = useState(null);

  // Prefill the acceptance identity from the trip's lead (quote -> trip -> lead)
  // instead of a hardcoded demo client. Editable — staff can correct on accept.
  const { data: trip } = useQuery({
    queryKey: ["quote-trip", quote.trip_request_id],
    queryFn: () => admin.trip(quote.trip_request_id),
    enabled: !!quote.trip_request_id,
    retry: false,
  });
  const { data: lead } = useQuery({
    queryKey: ["quote-lead", trip?.lead_id],
    queryFn: () => admin.lead(trip.lead_id),
    enabled: !!trip?.lead_id,
    retry: false,
  });
  useEffect(() => {
    setEmail(lead?.email || "");
    setName(lead?.full_name || "");
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async (option_id) => {
    if (!email || !name) {
      toast.error("Client name and email are required to accept");
      return;
    }
    setAccepting(option_id);
    try {
      const r = await admin.acceptQuote(quote.id, { option_id, client_email: email, client_name: name });
      toast.success(r.compliance === "manual_review_required" ? "Routed to compliance" : "Booked. Contract sent.");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to accept");
    } finally {
      setAccepting(null);
    }
  };

  return (
    <div data-testid={`quote-detail-${quote.id}`}>
      <div className="pj-label mb-2">Quote · {shortId(quote.id)}</div>
      <h2 className="font-display text-[20px] mb-1">3 branded options</h2>
      <div className="mono text-[11px] text-[rgb(var(--dim))] mb-4">
        Status: <StatusPill state={quote.status} /> · Generated in {quote.generated_in_ms || 0}ms
      </div>

      {quote.llm_summary && (
        <div className="mono text-[12px] text-[rgb(var(--fg))] mb-4 pj-card p-3 break-words" style={{ background: "rgb(var(--surface-2))" }}>
          {quote.llm_summary}
        </div>
      )}

      <div className="space-y-3">
        {quote.options?.map((o) => (
          <div key={o.id} className="pj-card p-3 flex items-start justify-between gap-3" data-testid={`quote-option-${o.tier}`}>
            <div className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-[rgb(var(--dim))] truncate">{o.tier}</div>
              <div className="font-display text-[15px] mt-1 truncate" title={o.model_name}>{o.model_name}</div>
              <div className="mono text-[10px] text-[rgb(var(--dim))] capitalize mt-0.5 truncate">
                {o.cabin.replace("_"," ")} · {o.seats} seats · ETA {o.eta_minutes}m
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="mono text-[16px]">{fmtUSD(o.total_usd)}</div>
              <button
                type="button"
                onClick={() => accept(o.id)}
                disabled={accepting === o.id || quote.status === "accepted" || !email || !name}
                data-testid={`btn-accept-${o.tier}`}
                style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
                className="mt-2 mono px-2 py-1 text-[10px] uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1"
              >
                <CheckCircle2 size={11} /> {accepting === o.id ? "Booking…" : "Accept"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <label className="block mt-4">
        <span className="pj-label block mb-1.5">Client name (required, prefilled from lead)</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="quote-client-name"
          className="w-full mono text-[12px] px-2 py-2"
          style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
        />
      </label>
      <label className="block mt-3">
        <span className="pj-label block mb-1.5">Client email (required, prefilled from lead)</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="quote-client-email"
          className="w-full mono text-[12px] px-2 py-2"
          style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
        />
      </label>

      <button
        type="button"
        onClick={() => admin.followUp(quote.id).then(() => toast.success("Follow-up nudge sent (mock)"))}
        data-testid="btn-follow-up"
        className="mt-3 mono w-full inline-flex items-center justify-center gap-2 px-2 py-2 text-[11px] uppercase tracking-[0.14em] border border-[rgb(var(--border-strong))] text-[rgb(var(--fg))] hover:text-[rgb(169,130,28)] hover:border-[rgb(169,130,28)]"
      >
        <MailCheck size={12} /> Send follow-up nudge
      </button>

      <div className="mt-4 pj-card p-3" style={{ background: "rgb(var(--surface-2))" }}>
        <div className="pj-label mb-1">14 CFR Part 295 Disclosure</div>
        <p className="mono text-[10px] leading-[1.55] text-[rgb(var(--dim))]">{quote.part295_disclosure}</p>
      </div>
    </div>
  );
}
